import {Button, Card, CardActions, CardContent, Grid, MenuItem, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import * as Yup from "yup";
import dayjs from "dayjs";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {getPastYears} from "../../../utils/FinanceDateUtils";
import {Controller, SubmitHandler, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {FormSelect} from "../../../experiments/forms/FormSelect";
import {IntermediariesInterface} from "../constants/IntermediariesModels";
import {useIntermediariesPrintoutRepository} from "../../../api/repositories/IntermediariesPrintoutRepository";
import {useIntermediariesReportRepository} from "../../../api/repositories/IntermediariesReportRepository";
import {getQuarterly} from "../tables/ReportTableUtil";
import MultiSelectIntermediaries from "./MultiSelectIntermediaries";
import FinanceDialog from "../../../component/FinanceDialog";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";

type PrintoutInputs = {
    year: number,
    quarterly: string,
    legalEntity: string,
    dealer: string,
    intermediaries: any[]
}

const CreateIntermediariesPrintout = () => {
 const { t: tg } = useTranslation("translation");

    const { dispatchGlobalData, globalData: { user } } = useContext(GlobalContext);

    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
        value: x.name,
        label: tg(`legalEntities.${x.name}`),
        }));


    const dealerOptions: any[] = user.dealers
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
        value: x.name,
        label: tg(`dealers.${x.name}`),
    }));
    
    const [dealers, setDealers] = useState<any[]>(dealerOptions);

    const {t} = useTranslation("finance", {keyPrefix: 'intermediaries'});

    const {postPrintoutRequest} = useIntermediariesReportRepository();
    const {getIntermediaries} = useIntermediariesPrintoutRepository();
    const [openWarningEmail, setOpenWarningEmail] = useState(false);
    const [warningIntermediaries, setWarningIntermediaries] = useState<any>([]);
    const [payloadIntermediaries, setPayloadIntermediaries] = useState<any>();

    const navigate = useNavigate();

    const [availableIntermediaries, setAvailableIntermediaries] = useState<IntermediariesInterface[]>([]);

    const schema = Yup.object().shape({
        year: Yup.number(),
        quarterly: Yup.string().required(),
        legalEntity: Yup.string().required(),
        dealer: Yup.string().required(),
        intermediaries: Yup.array().required(),
    });

    const defaultValues: PrintoutInputs = {
        year: dayjs().month() > 0 ? dayjs().year() : dayjs().year() - 1,
        quarterly: "Q1",
        legalEntity: legalEntityOptions.length == 1 ? legalEntityOptions[0].value : "",
        dealer: dealerOptions.length == 1 ? dealerOptions[0].value : "",
        intermediaries: []
    }

    const {handleSubmit, control, setValue, clearErrors, watch} = useForm<PrintoutInputs>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema)
    });

    const handleChangeLegalEntities = (value: any) => {
        if (value == '') {
            setDealers([]);
            setValue('dealer', '');
            setValue('intermediaries', []);
            setAvailableIntermediaries([]);
            return;
        }
        let dealers = user?.dealers
            ?.filter(
                (x: any) =>
                    x.legalEntities.some((e: any) => value == e.name) &&
                    x.name.toLowerCase() !== "all"
            )
            .map((x: any) => {
            return {
                label: tg(`dealers.${x.name}`),
                value: x.name,
            };
        });
        setDealers(dealers);
    }

    useEffect(() => {
        let legalEntity = watch("legalEntity");
        let dealer = watch("dealer");
        if(legalEntity === "" || dealer === "") {
            setAvailableIntermediaries([]);
            setValue('intermediaries', []);
            return;
        }
        getIntermediaries(legalEntity, dealer).then((response: any) => {
            setValue('intermediaries', []);
            setAvailableIntermediaries([]);

            if (response.statusCode === "OK")
                setAvailableIntermediaries(response.body);
        });

    }, [watch("legalEntity"), watch("dealer")]);

    const onSubmit: SubmitHandler<PrintoutInputs> = (data) => {
        let payload = {...data};
        if(payload.intermediaries.length > 0){
            payload.intermediaries = availableIntermediaries.filter(e =>
                (data.intermediaries as string[]).includes(e.intermediaryId));

            let intermediariesNoEmail = payload.intermediaries.filter(intermediary => intermediary.intermediaryEmail === "").map(i => i.intermediaryName);

            if(intermediariesNoEmail.length > 0){
                setWarningIntermediaries(intermediariesNoEmail)
                setOpenWarningEmail(true);
                setPayloadIntermediaries(payload)
            } else {
                createPrintouts(payload);
            }
        } else {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('printout-wizard.snackbars.handleErrors.emptyIntermediaries'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        }
    }

    function createPrintouts(payload :any)  {
        postPrintoutRequest(payload)
            .then((data) => {
                    if (data.status === "OK") {
                        dispatchGlobalData({
                            type: setSnackbar,
                            payload: {
                                snackbar: {
                                    message: t('printout-wizard.snackbars.success'),
                                    type: snackbarTypes.success,
                                    show: true,
                                    autoClose: true
                                }
                            }
                        });
                        navigate('/finance/intermediaries');
                    } else {
                        let message = t( data.message ?  `printout-wizard.snackbars.handleErrors.${data.message}` : 'api.snackbars.errors.generic');
                        let messageString = data.message;
                        if (messageString.includes("intermediaryName")){
                            let messageObject = JSON.parse(messageString);
                            if (messageObject.intermediaryName) {
                                let intermediaryName = messageObject.intermediaryName;
                                message = t('printout-wizard.snackbars.handleErrors.NoInvoiceForIntermediariesException', { intermediaries: intermediaryName });
                            }
                        }
                        dispatchGlobalData({
                            type: setSnackbar,
                            payload: {
                                snackbar: {
                                    message: message,
                                    type: snackbarTypes.error,
                                    show: true,
                                    autoClose: true
                                }
                            }
                        });
                    }
                }
            );
    }

    return (
        <Card variant="outlined" sx={{margin: '15px'}}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <CardContent>
                    <Grid container spacing={2} alignItems={'flex-end'}>
                        <Grid item xs={12}>
                            <Typography variant={'body2'} color={'primary.light'} fontWeight={700}>
                                {t('printout-wizard.form.title').toUpperCase()}
                            </Typography>
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormSelect
                                name={'year'}
                                control={control}
                                props={{
                                    variant: "standard", fullWidth: true, label: t('printout-wizard.form.year')
                                }}
                                items={getPastYears(10, dayjs().month() === 0 ? dayjs().year() - 1 : dayjs().year()).reverse().map((it: number) => (
                                    <MenuItem key={it} value={it}>
                                        {it}
                                    </MenuItem>
                                ))}/>

                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormSelect
                                name={'quarterly'}
                                control={control}
                                props={{
                                    fullWidth: true,
                                    label: t('printout-wizard.form.quarterly')
                                }}
                                items={getQuarterly().map((it: string, index) => (
                                    <MenuItem key={it} value={"Q" + (index + 1)}>
                                        {it.charAt(0).toUpperCase() + it.slice(1)}
                                    </MenuItem>
                                ))}/>
                        </Grid>
                         <Grid item md={3} sm={6} xs={12}>
                            <FormSelect
                                name={"legalEntity"}
                                control={control}
                                props={{
                                    label: tg("shared.legalEntity"),
                                    fullWidth: true,
                                    required: true,
                                    onChange: (e: any) => {
                                        handleChangeLegalEntities(e.target.value)
                                    },
                                }}
                                
                                items={legalEntityOptions.map((option: any) => (
                                    <MenuItem key={option.value} 
                                                value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            />                       
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                        <FormSelect
                                name={"dealer"}
                                control={control}
                                props={{
                                    label: tg("shared.dealer"),
                                    fullWidth: true,
                                    required: true,
                                    disabled: dealers.length == 0
                                }}
                                items={dealers.map((option: any) => (
                                    <MenuItem key={option.value}
                                                value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            />
                        </Grid>
                        <Grid item md={12} sm={12} xs={12}>
                            <Controller
                                control={control}
                                name="intermediaries"
                                render={({ field, fieldState: { error } }) => (
                                    <MultiSelectIntermediaries items={availableIntermediaries.map(c => c.intermediaryId)}
                                                 itemsName={availableIntermediaries.map(c => c.intermediaryName)}
                                                 label={t("printout-wizard.form.intermediaries")} fieldName={"intermediaries"}
                                                 setValue={setValue}
                                                 selectAllLabel={t('printout-wizard.form.selectAll')}
                                                 errorMessage={error?.message} clearErrors={clearErrors}
                                                 disabled={watch('legalEntity') === '' || watch('dealer') === ''}
                                    />
                                )}/>

                        </Grid>
                    </Grid>
                </CardContent>
                <CardActions sx={{justifyContent: 'flex-end'}}>
                    <Button variant={"outlined"} color={"accent"} onClick={() => navigate('/finance/intermediaries')}>
                        {t('printout-wizard.buttons.cancel')}</Button>
                    <Button color={"accent"} type="submit">{t('printout-wizard.buttons.createPrintouts')}</Button>
                </CardActions>
            </form>
            <FinanceDialog title={t('preview.warningIntermediaries.title')}
                           content={t('preview.warningIntermediaries.content', {intermediariesWithoutEmail: warningIntermediaries})}
                           open={openWarningEmail}
                           onClose={() => setOpenWarningEmail(false)}
                           callback={() => createPrintouts(payloadIntermediaries)}
                           cancelButton={t('preview.warningIntermediaries.cancel')}
                           confirmButton={t('preview.warningIntermediaries.confirm')}
                           titleIcon={<InfoRoundedIcon/>}/>
        </Card>
    )
}

export default CreateIntermediariesPrintout;